import {ReactNode} from 'react';
import {History} from 'history';
import {Dispatch} from 'redux';
import {PagingParams} from '@pluto/table';
import {SetQuery} from '@pluto/set-history-query';
import {ContentPartnerPayload} from '@pluto/content-partners';
import {WFState} from 'MediaOrders/MediaOrdersTypes';

export interface ContentItemSearchPayload {
    rows: ContentItemSearch[];
    total: number;
    isLoading: boolean;
}

export interface ContentItemSearchResponse {
    payload: ContentItemSearchPayload;
}

export interface ContentItemSearch {
    id: number;
    priority: number;
    dueDate: string;
    startDate?: null;
    finishDate?: null;
    status: string;
    statusHistory?: StatusHistoryEntity[];
    type: string;
    mediaQcStatus: string;
    metadataQcStatus: string;
    artworkQcStatus: string;
    deliveryStartedAt?: null;
    userId?: null;
    assignedAt?: null;
    createdAt: string;
    updatedAt: string;
    deletedAt?: null;
    contentItemId: number;
    assignee?: null;
    issues?: null[] | null;
    contentItem: ContentItem;
    mamId: string;
}

export interface StatusHistoryEntity {
    status: string;
    timestamp: string;
}
export interface ContentItem {
    contentItemGenres?: string[];
    contentItemSubGenres?: string[];
    displayTitle: string;
    id: number;
    dataVersion: number;
    releaseDate?: string | null;
    lastPublishedDate?: string | null;
    copyrightYear?: number | null;
    allotment: number;
    number?: number | null;
    plutotvAvailsId?: string | null;
    copyrightHolder?: string | null;
    contentItemType: string;
    roviId?: null;
    tmsId?: null;
    governingId?: null;
    labPartnerId?: string | null;
    mamId: string;
    programmingType?: string | null;
    sourcePartnerId?: string | null;
    genres?: string[];
    subGenres?: string[];
    ingestType: string;
    labPartner?: string | null;
    sourceRegion?: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt?: null;
    contentPartnerId: number;
    contentPartner: ContentPartnerPayload;
    title: string;
    seasonNumber?: number | null;
    seriesTitle?: string | null;
    workflowState: WFState;
}

export interface ComponentStateType extends ComponentFilterTypes, Filters {
    search: string;
    rowsPerPage: number;
    page: number;
    onRequest: Function;
    setSearchQuery: Function;
}

export interface ComponentFilterTypes {
    contType: string[];
    contPartner: string[];
    labPartner: string[];
    genre: string[];
    lang: string[];
    rating: string[];
    category: string[];
    region: string[];
    status: string[];
}

export interface ComponentFilterSetters {
    filtersSetters: Function[];
    clearFiltersInputs: boolean;
    setClearFiltersInputs: Function;
}

export interface Filters {
    filters: {[key: string]: string[]};
}

export interface SearchMountHandler extends ComponentFilterTypes, Filters {
    getContentPartners: Function;
    getSearchItems: Function;
    getGenres: Function;
    getCategories: Function;
    categoriesLoading: boolean;
    contentPartnersLoading: boolean;
    genresLoading: boolean;
    search: string;
    limit: number;
    offset: number;
    dispatch: Dispatch;
}

export interface SearchResetHandler {
    getSearchItems: Function;
    setPage: Function;
    resetFilters: Function;
    history: History;
    search: string;
    limit: number;
    route: string;
    setQuery: SetQuery<{}>;
    dispatch: Dispatch;
}

export interface SearchHandler extends ComponentFilterTypes, Filters {
    onRequest: Function;
    setSearchQuery: Function;
    search: string;
    rowsPerPage: number;
}

export interface SearchGroupLoadingProps {
    title: string;
    isPanelOpen: boolean;
    setPanelOpen: () => void;
    expandMoreIcon: JSX.Element;
}

export interface SearchGroupProps extends SearchGroupLoadingProps {
    listRenderer: () => ReactNode;
    filterable: boolean;
    resetFilter: Function;
    selectedValues: string[];
    minHeight?: number;
}

type GroupStatePropsOption = {value: string; label: string};

export interface GroupStateProps {
    options: GroupStatePropsOption[];
    selectedValues: string[];
    onChange: () => void;
    filterable?: boolean;
}

export type OptionsDataType = {
    isLoading: boolean;
    data: GroupStatePropsOption[];
};

export interface SearchFiltersHandlersType {
    optionsData: OptionsDataType;
    selectedValues: string[];
    onChange: () => void;
    clearFilter?: boolean;
    resetFilter: Function;
}

export interface SearchGroupFilterableProps
    extends SearchGroupLoadingProps,
        SearchFiltersHandlersType {
    createItemData: ({options, selectedValues, onChange}: GroupStateProps) => void;
    clearFilter?: boolean;
    resetFilter: Function;
}

export type HandleRequestParamsType = {[key: string]: string | number | string[]};

export interface RenderTitle {
    displayTitle: string;
    contentItemType: string;
    id: string;
    mamId: string;
}

export enum FilterTypes {
    search = 'search',
    contType = 'contType',
    contPartner = 'contPartner',
    labPartner = 'labPartner',
    genre = 'genre',
    lang = 'lang',
    rating = 'rating',
    category = 'category',
    region = 'region',
    status = 'status',
}

export enum ContentItemsSearch {
    title = 'title',
    genre = 'genre',
    contentPartnerId = 'contentPartnerId',
    contentItemType = 'contentItemType',
    language = 'language',
    labPartner = 'labPartner',
    rating = 'rating',
    category = 'category',
    countryCode = 'countryCode',
}
export enum FilterLabels {
    contType = 'Content Types',
    contPartner = 'Content Partners',
    labPartner = 'Lab Partners',
    lang = 'Metadata Languages',
    genre = 'Genres',
    rating = 'Ratings',
    category = 'Categories',
    region = 'Regions',
    status = 'Workflow Status',
}

export enum IconTypes {
    film = 'film',
    series = 'series',
    webOriginal = 'web-original',
}

export type IconTypesKeys = keyof Record<IconTypes, string>;

type SearchedContentFilters = {[key in keyof typeof FilterLabels]: string[]};

export interface SearchedContentRequest extends PagingParams, SearchedContentFilters {
    search: string;
}

export interface ContentItemsSearchRequest extends PagingParams {
    title: string;
    genre: string[];
    contentPartnerId: string[];
    contentItemType: string[];
    language: string[];
    labPartner: string[];
    rating: string[];
    category: string[];
    countryCode: string[];
    workflowState: string[];
}

export type TextEnum = {[key: string]: string};

export type StringTulip = [string, string];

export type SelectOption = {value: string | unknown; label: string | unknown};

export type Statuses = {[key: string]: string};

export type EnumMapper = ([value, label]: StringTulip) => SelectOption;
